// src/components/EfficiencyPerformanceMetrics.js
import React from 'react';
import { formatNumber } from '../utils/format';
import definitions from '../definitionPlaceholders.json';
import Tooltip from './Tooltip';

const EfficiencyPerformanceMetrics = ({ selectedCompany, stockSymbol, metricsData }) => {
  const defaultMetrics = {
    // Profitability
    'Gross Margin': 'N/A',
    'EBITDA Margin': 'N/A',
    'Net Profit Margin': 'N/A',
    'NOPAT Margin': 'N/A',

    // Efficiency
    'Asset Turnover': 'N/A',
    'Inventory Turnover': 'N/A',
    'DSO (Days Sales Outstanding)': 'N/A',
    'CCC (Cash Conversion Cycle)': 'N/A',

    // Growth
    'Revenue Growth': 'N/A',
    'Net Income Growth': 'N/A',
    'EPS Growth (%)': 'N/A',

    // Cash flow
    'Free Cash Flow': 'N/A',
  };

  // UI label → backend data key (for numeric retrieval/formatting)
  const dataKeyMap = {
    'Gross Margin': 'grossMarginPct',
    'EBITDA Margin': 'ebitdaMarginPct',
    'Net Profit Margin': 'netProfitMarginPct',
    'NOPAT Margin': 'nopatMarginPct',

    'Asset Turnover': 'assetTurnover',
    'Inventory Turnover': 'inventoryTurnover',
    'DSO (Days Sales Outstanding)': 'dsoDays',
    'CCC (Cash Conversion Cycle)': 'cccDays',

    'Revenue Growth': 'revenueGrowthPct',
    'Net Income Growth': 'netIncomeGrowthPct',
    'EPS Growth (%)': 'epsGrowthPct',

    'Free Cash Flow': 'freeCashFlow',
  };

  // UI label → definition key (for tooltip text)
  const definitionKeyMap = {
    'Gross Margin': 'grossProfitRatio',
    'EBITDA Margin': 'ebitdaRatio',
    'Net Profit Margin': 'netIncomeRatio',
    'NOPAT Margin': 'nopatMargin',

    'Asset Turnover': 'assetTurnover',
    'Inventory Turnover': 'inventoryTurnover',
    'DSO (Days Sales Outstanding)': 'dso',
    'CCC (Cash Conversion Cycle)': 'ccc',

    'Revenue Growth': 'revenueGrowth',
    'Net Income Growth': 'netIncomeGrowth',
    'EPS Growth (%)': 'epsGrowthPct',

    'Free Cash Flow': 'freeCashFlow',
  };

  // Build merged display object from predefined labels
  const displayMetrics = {};
  Object.entries(defaultMetrics).forEach(([label, fallback]) => {
    const dataKey = dataKeyMap[label];
    const value =
      dataKey &&
      metricsData &&
      metricsData[dataKey] !== undefined &&
      metricsData[dataKey] !== null
        ? metricsData[dataKey]
        : fallback;

    displayMetrics[label] = value !== undefined && value !== null ? value : fallback;
  });

  return (
    <div className="w-full my-4">
      <div className="card p-5 min-h-[625px]">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-6 bg-advanced-efficiency rounded-full" />
          <h2 className="section-heading">
            Profitability | Efficiency | Growth{selectedCompany ? ` | ${selectedCompany}` : ''}{stockSymbol ? ` | ${stockSymbol}` : ''}
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-3 w-full min-w-0">
          {Object.keys(displayMetrics).map((label, index) => {
            const defKey = definitionKeyMap[label];
            const defText = defKey ? definitions[defKey] : null;

            return (
              <div
                key={index}
                className="relative bg-advanced-efficiency-light border border-amber-200 p-4 rounded-lg min-h-[96px] card-hover overflow-x-hidden overflow-y-visible"
              >
                {defText && (
                  <Tooltip content={defText}>
                    <span
                      className="absolute top-2 left-2 text-xs text-gray-500 cursor-help"
                      tabIndex={0}
                      aria-label={`Definition for ${label}`}
                    >
                      ℹ️
                    </span>
                  </Tooltip>
                )}

                <div className="absolute bottom-2 left-2 right-2 text-xs sm:text-sm font-medium text-gray-600 break-words">
                  {label}
                </div>

                <div className="absolute top-5 right-3 left-8 text-sm sm:text-lg font-semibold text-advanced-efficiency-dark text-right break-words">
                  {formatNumber(label, displayMetrics[label], dataKeyMap[label])}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EfficiencyPerformanceMetrics;











